import {
  ChangeDetectionStrategy,
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { provideCalendarDayDateAdapter } from '@core/services/calendar-day-date-adapter';
import { CalendarDay } from '@shared/models/dateTypes';
import { AbookFilterChipComponent } from './abook-filter-chip.component';
import { PeriodValue } from './filter-state';
import {
  PAST_PERIOD_SHORTCUTS,
  PERIOD_SHORTCUTS,
  PeriodShortcut,
  matchShortcut,
  periodText,
} from './period-shortcuts';

/** Which date a period applies to — "Bookingens tid" or "Oprettelse" (replaces "Søger i", R5). */
export interface PeriodDateType<T extends string | number = number> {
  value: T;
  /** In the panel's toggle. */
  label: string;
  /** On the chip: "Periode", "Oprettet". */
  chipLabel: string;
  /** A date that can only lie behind us ("Oprettelse"): past shortcuts, no future days. */
  past?: boolean;
}

/** Slutdato before startdato. Material's own range check only fires inside its picker. */
const endAfterStart: ValidatorFn = (group): ValidationErrors | null => {
  const { start, end } = group.value as { start: CalendarDay | null; end: CalendarDay | null };
  return start && end && end < start ? { endBeforeStart: true } : null;
};

/**
 * The period filter (R5): one chip, one panel with the date type, six shortcuts and Fra–til as one
 * field with one calendar. Dates are DD-MM-ÅÅÅÅ via `CalendarDayDateAdapter`; "i dag" is Danish
 * today. An invalid period is marked in the panel and reported through {@link invalidChange}, so the
 * table can say why it is empty (R15).
 */
@Component({
  selector: 'abook-period-chip',
  imports: [
    AbookFilterChipComponent,
    OverlayModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  providers: [...provideCalendarDayDateAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.is-changed]': 'changed() || invalid()' },
  template: `
    <abook-filter-chip
      #chip
      icon="calendar_today"
      [label]="chipLabel()"
      [value]="invalid() ? 'Ugyldig' : valueText()"
      [changed]="changed() || invalid()"
      [compact]="compact()"
      [expanded]="open()"
      (opened)="open.set(!open())"
      (cleared)="clear()"
    />

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="chip.origin()!"
      [cdkConnectedOverlayOpen]="open() && !!chip.origin()"
      [cdkConnectedOverlayPositions]="positions"
      [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      [cdkConnectedOverlayPush]="true"
      (backdropClick)="open.set(false)"
      (overlayKeydown)="$event.key === 'Escape' && open.set(false)"
      (detach)="open.set(false)"
    >
      <div
        class="abook-filter-panel abook-period-panel"
        role="dialog"
        [attr.aria-label]="chipLabel()"
      >
        @if (dateTypes().length > 1) {
          <mat-button-toggle-group
            class="abook-period-panel__types"
            [value]="dateType()"
            (change)="dateTypeChange.emit($event.value)"
            [hideSingleSelectionIndicator]="true"
            aria-label="Datotype"
          >
            @for (type of dateTypes(); track type.value) {
              <mat-button-toggle [value]="type.value">{{ type.label }}</mat-button-toggle>
            }
          </mat-button-toggle-group>
        }

        <div class="abook-period-panel__shortcuts" role="group" aria-label="Genveje">
          @for (shortcut of shortcuts(); track shortcut.id) {
            <button
              type="button"
              class="abook-period-panel__shortcut"
              [class.is-active]="activeShortcut()?.id === shortcut.id"
              [attr.aria-pressed]="activeShortcut()?.id === shortcut.id"
              (click)="pick(shortcut)"
            >
              {{ shortcut.label }}
            </button>
          }
        </div>

        <mat-form-field
          class="abook-period-panel__range"
          appearance="outline"
          subscriptSizing="dynamic"
        >
          <mat-label>Fra – til</mat-label>
          <mat-date-range-input [formGroup]="form" [rangePicker]="picker" [max]="maxDay()">
            <input
              matStartDate
              formControlName="start"
              placeholder="DD-MM-ÅÅÅÅ"
              (dateChange)="commit()"
            />
            <input
              matEndDate
              formControlName="end"
              placeholder="DD-MM-ÅÅÅÅ"
              (dateChange)="commit()"
            />
          </mat-date-range-input>
          <mat-datepicker-toggle matIconSuffix [for]="picker" />
          <mat-date-range-picker
            #picker
            (opened)="pickerOpen = true"
            (closed)="pickerOpen = false; commit()"
          />
          @for (error of errors(); track error) {
            <mat-error>{{ error }}</mat-error>
          }
        </mat-form-field>
      </div>
    </ng-template>
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
})
export class AbookPeriodChipComponent<T extends string | number = number> {
  private readonly adapter = inject<DateAdapter<CalendarDay>>(DateAdapter);

  readonly value = input.required<PeriodValue>();
  /** The chip's name when there is no date type: "Periode". */
  readonly label = input('Periode');
  readonly dateTypes = input<readonly PeriodDateType<T>[]>([]);
  readonly dateType = input<T | null>(null);
  /** The value with no dates at all: "Alle", or "Når som helst" for a creation date. */
  readonly anyLabel = input('Alle');
  /**
   * A date that can only lie behind us — when a user was created (#861). Past shortcuts ("Sidste 7
   * dage") and no day after today in the calendar. A date type can say it for itself (`past`).
   */
  readonly past = input(false, { transform: booleanAttribute });
  readonly changed = input(false, { transform: booleanAttribute });
  readonly compact = input(false, { transform: booleanAttribute });

  readonly valueChange = output<PeriodValue>();
  readonly dateTypeChange = output<T>();
  readonly invalidChange = output<boolean>();
  readonly cleared = output<void>();

  protected readonly open = signal(false);
  private readonly isPast = computed(
    () => this.dateTypes().find((t) => t.value === this.dateType())?.past ?? this.past(),
  );
  protected readonly shortcuts = computed(() =>
    this.isPast() ? PAST_PERIOD_SHORTCUTS : PERIOD_SHORTCUTS,
  );
  /** Danish today for a past period — the calendar offers nothing after it. */
  protected readonly maxDay = computed(() => (this.isPast() ? this.adapter.today() : null));
  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    // Right-aligned when the chip sits near the band's right edge.
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  protected readonly form = new FormGroup(
    {
      start: new FormControl<CalendarDay | null>(null),
      end: new FormControl<CalendarDay | null>(null),
    },
    { validators: endAfterStart },
  );
  private readonly formStatus = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  protected readonly invalid = computed(() => this.formStatus() === 'INVALID');

  protected readonly errors = computed(() => {
    this.formStatus();
    const errors: string[] = [];
    if (this.form.controls.start.hasError('matDatepickerParse')) errors.push('Ugyldig startdato');
    if (this.form.controls.end.hasError('matDatepickerParse')) errors.push('Ugyldig slutdato');
    if (
      this.form.hasError('endBeforeStart') ||
      this.form.controls.end.hasError('matEndDateInvalid')
    ) {
      errors.push('Slutdato ligger før startdato');
    }
    if (
      this.form.controls.start.hasError('matDatepickerMax') ||
      this.form.controls.end.hasError('matDatepickerMax')
    ) {
      errors.push('Perioden kan ikke ligge efter i dag');
    }
    return errors;
  });

  protected readonly chipLabel = computed(
    () => this.dateTypes().find((t) => t.value === this.dateType())?.chipLabel ?? this.label(),
  );
  protected readonly valueText = computed(() =>
    periodText(this.value(), this.adapter.today(), this.anyLabel(), this.shortcuts()),
  );
  // Not while the field shows something else than the value: an invalid period matches nothing.
  protected readonly activeShortcut = computed(() =>
    this.invalid()
      ? undefined
      : matchShortcut(this.value(), this.adapter.today(), this.shortcuts()),
  );

  constructor() {
    // The page's value is the truth; the form follows it (a reset, back/forward, a shortcut).
    effect(() => {
      const { from, to } = this.value();
      const { start, end } = this.form.getRawValue();
      if (start !== from || end !== to)
        this.form.setValue({ start: from, end: to }, { emitEvent: true });
    });

    let lastInvalid = false;
    effect(() => {
      const invalid = this.invalid();
      if (invalid !== lastInvalid) this.invalidChange.emit((lastInvalid = invalid));
    });
  }

  protected pick(shortcut: PeriodShortcut): void {
    this.valueChange.emit(shortcut.range(this.adapter.today()));
    this.open.set(false);
  }

  /**
   * × — the page puts the value back to its standard. If the value already was the standard (the
   * field only held an invalid entry), the value does not change and the form would keep the
   * invalid text; so the form goes back to the value here as well.
   */
  protected clear(): void {
    const { from, to } = this.value();
    this.form.setValue({ start: from, end: to });
    this.cleared.emit();
  }

  /** Set while the calendar is open: its first click is only half a period. */
  protected pickerOpen = false;

  /**
   * A typed or picked date. While the calendar is open it waits for the second click (or for the
   * calendar to close), so a range costs one fetch, not two. An invalid period is never sent.
   */
  protected commit(): void {
    if (this.pickerOpen || this.form.invalid) return;
    const { start, end } = this.form.getRawValue();
    const current = this.value();
    if (start !== current.from || end !== current.to)
      this.valueChange.emit({ from: start, to: end });
  }
}
